import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import { plusIcon } from '@progress/kendo-svg-icons';
import {
    DlpLookupModel, DlpQuotaProfileRowModel, DlpScopeBrandModel, DlpScopeModel
} from 'services/DlpConfiguration/DlpConfigurationService';
import { DlpIssue, DlpScopeStatus, scopeStatus } from 'utils/dlpConfigurationValidation';
import { IssueBadges } from '../Common/IssueBadges';
import { ScopeStatusBadge } from '../Common/ScopeStatusBadge';
import { ScopeDetail } from './ScopeDetail';

type StatusFilter = 'all' | DlpScopeStatus;
const STATUS_FILTERS: StatusFilter[] = ['all', 'active', 'future', 'past'];

// "All" deliberately hides past scopes: they only show up under their own filter.
const matchesFilter = (scope: DlpScopeModel, filter: StatusFilter) =>
    filter === 'all' ? scopeStatus(scope) !== 'past' : scopeStatus(scope) === filter;

interface ScopesPanelProps {
    // Scopes, brand links and profiles of the current project only
    scopes: DlpScopeModel[];
    scopeBrands: DlpScopeBrandModel[];
    profiles: DlpQuotaProfileRowModel[];
    brands: DlpLookupModel[];
    countries: DlpLookupModel[];
    issues: DlpIssue[];
    selectedId: number | undefined;
    onSelect: (scopeId: number) => void;
    onCreate: () => Promise<void>;
    onSave: (scope: DlpScopeModel, brandIds: string[]) => Promise<void>;
    onDelete: (scopeId: number) => Promise<void>;
    onDirtyChange: (dirty: boolean) => void;
}

export function ScopesPanel({ scopes, scopeBrands, profiles, brands, countries, issues, selectedId, onSelect, onCreate, onSave, onDelete, onDirtyChange }: ScopesPanelProps) {
    const { t } = useTranslation();
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

    const visibleScopes = scopes.filter(s => matchesFilter(s, statusFilter));
    const scope = scopes.find(s => s.scope_id === selectedId) ?? visibleScopes[0];
    const profileNames = Array.from(new Set(profiles.map(p => p.profile_name)));

    const brandsFor = (scopeId: number) => scopeBrands.filter(sb => sb.scope_id === scopeId).map(sb => sb.brand_id);
    const brandName = (brandId: string) => brands.find(b => b.id === brandId)?.name ?? brandId;
    const issuesFor = (scopeId: number) => issues.filter(i => i.targetId === scopeId);

    const brandSummary = (s: DlpScopeModel) => {
        const names = s.all_brands ? [t('dlpConfiguration.scopes.allBrands').toUpperCase()] : brandsFor(s.scope_id).map(brandName);
        const preview = names.slice(0, 3).join(', ') + (names.length > 3 ? '…' : '');
        return `${t('dlpConfiguration.scopes.brandCount', { count: names.length })} · ${preview}`;
    };

    return (
        <div>
            <button type="button" className="lm-btn-primary" style={{ marginBottom: 16 }} onClick={onCreate}>
                <SvgIcon icon={plusIcon} /> <Trans>dlpConfiguration.scopes.addScope</Trans>
            </button>

            <div className="status-filter-row">
                {STATUS_FILTERS.map(filter => (
                    <button
                        key={filter}
                        type="button"
                        className={`status-filter-btn ${statusFilter === filter ? 'active' : ''}`}
                        onClick={() => setStatusFilter(filter)}
                    >
                        {t(`dlpConfiguration.scopes.status.${filter}`)}
                        <span className="status-filter-count">{scopes.filter(s => matchesFilter(s, filter)).length}</span>
                    </button>
                ))}
            </div>

            <div className="two-col">
                <div className="list-col">
                    {visibleScopes.map(s => (
                        <button
                            type="button"
                            key={s.scope_id}
                            aria-pressed={s.scope_id === scope?.scope_id}
                            className={`list-item ${s.scope_id === scope?.scope_id ? 'active' : ''}`}
                            onClick={() => s.scope_id !== scope?.scope_id && onSelect(s.scope_id)}
                        >
                            <span className="list-item-title">
                                {s.name}
                                <ScopeStatusBadge status={scopeStatus(s)} />
                            </span>
                            <span className="list-item-sub">{brandSummary(s)}</span>
                            <span className="list-item-badges"><IssueBadges issues={issuesFor(s.scope_id)} compact /></span>
                        </button>
                    ))}
                    {visibleScopes.length === 0 && (
                        <div className="empty-note">
                            {scopes.length === 0
                                ? t('dlpConfiguration.scopes.emptyProject')
                                : t('dlpConfiguration.scopes.emptyFilter', { status: t(`dlpConfiguration.scopes.status.${statusFilter}`) })}
                        </div>
                    )}
                </div>

                {scope && (
                    <ScopeDetail
                        key={scope.scope_id}
                        scope={scope}
                        brandIds={brandsFor(scope.scope_id)}
                        brandsUsedElsewhere={scopeBrands.filter(sb => sb.scope_id !== scope.scope_id).map(sb => sb.brand_id)}
                        profileNames={profileNames}
                        brands={brands}
                        countries={countries}
                        issues={issuesFor(scope.scope_id)}
                        isNew={scope.scope_id < 0}
                        onSave={onSave}
                        onDelete={onDelete}
                        onDirtyChange={onDirtyChange}
                    />
                )}
            </div>
        </div>
    );
}
